import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Snackbar, type AlertColor } from '@mui/material';

/**
 * One way to say "done".
 *
 *   const notify = useNotify();
 *   notify('Charge sheet approved.');
 *   notify('Could not post that.', { tone: 'error' });
 *   notify('Removed.', { action: { label: 'Undo', onClick: restore } });
 *
 * A toast at the bottom of the shell, one at a time, announced to screen
 * readers through the Alert's live region. Success and neutral notices
 * dismiss themselves; errors and warnings stay until dismissed, because a
 * refusal that vanishes before it is read is a refusal nobody saw.
 *
 * This replaces the inline success Alerts each page used to keep in its own
 * state: those sat in the page, said the same thing sixteen different ways,
 * and stayed there until the next render forgot them.
 */
export interface NotifyOptions {
  tone?: AlertColor;
  /** One optional follow-up, such as Undo or View. */
  action?: { label: string; onClick: () => void };
  /** Milliseconds before a success or info notice dismisses itself. */
  duration?: number;
}

type Notify = (message: string, options?: NotifyOptions) => void;

const NotifyContext = createContext<Notify | null>(null);

interface Toast extends NotifyOptions {
  key: number;
  message: string;
}

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const counter = useRef(0);

  const notify = useCallback<Notify>((message, options) => {
    counter.current += 1;
    setToast({ key: counter.current, message, ...options });
  }, []);

  const value = useMemo(() => notify, [notify]);
  const tone = toast?.tone ?? 'success';
  const sticky = tone === 'error' || tone === 'warning';

  return (
    <NotifyContext.Provider value={value}>
      {children}
      <Snackbar
        key={toast?.key}
        open={!!toast}
        autoHideDuration={sticky ? null : (toast?.duration ?? 4000)}
        onClose={(_, reason) => { if (reason !== 'clickaway') setToast(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Above the phone's bottom navigation bar and its home indicator.
        sx={{ bottom: { xs: 'calc(64px + env(safe-area-inset-bottom))', md: 24 } }}
        data-testid="notify"
      >
        <Alert
          severity={tone}
          variant="filled"
          onClose={() => setToast(null)}
          action={toast?.action ? (
            <>
              <Button color="inherit" size="small" onClick={() => { toast.action?.onClick(); setToast(null); }}>{toast.action.label}</Button>
            </>
          ) : undefined}
          sx={{ minWidth: 280, alignItems: 'center' }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </NotifyContext.Provider>
  );
}

export function useNotify(): Notify {
  const notify = useContext(NotifyContext);
  if (!notify) throw new Error('useNotify must be used within a NotifyProvider');
  return notify;
}
